// Enhanced logging utility for API monitoring and debugging
export interface LogContext {
  service: string;
  method: string;
  endpoint: string;
  userId?: number;
  username?: string;
  sessionId?: string;
  requestId?: string;
}

export interface ApiRequestLog extends LogContext {
  timestamp: string;
  params?: any;
  data?: any;
  headers?: any;
}

export interface ApiResponseLog extends LogContext {
  timestamp: string;
  status: number;
  duration: number;
  dataSize: number;
  success: boolean;
}

export interface ApiErrorLog extends LogContext {
  timestamp: string;
  status?: number;
  error: string;
  duration: number;
  serverResponse?: any;
  stack?: string;
}

export interface UserActionLog {
  timestamp: string;
  userId?: number;
  username?: string;
  action: string;
  entityType: string;
  entityId?: number;
  details?: any;
  result: 'success' | 'error' | 'pending';
}

export class Logger {
  private static isProduction = import.meta.env.PROD;
  private static isDevelopment = import.meta.env.DEV;
  
  // Generate unique request ID for tracking
  private static generateRequestId(): string {
    return `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }
  
  // Get current user context
  private static getUserContext() {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      return {
        userId: user.id,
        username: user.username || user.name
      };
    } catch {
      return {};
    }
  }
  
  // API Request Logging
  static logApiRequest(service: string, method: string, endpoint: string, params?: any, data?: any, headers?: any): string {
    const requestId = this.generateRequestId();
    const timestamp = new Date().toISOString();
    const userContext = this.getUserContext();
    
    const logData: ApiRequestLog = {
      timestamp,
      service,
      method: method.toUpperCase(),
      endpoint,
      requestId,
      ...userContext,
      params: this.sanitizeData(params),
      data: this.sanitizeData(data),
      headers: this.sanitizeHeaders(headers)
    };
    
    if (this.isDevelopment) {
      console.group(`🚀 [API REQUEST] ${method.toUpperCase()} ${endpoint}`);
      console.log('📝 Request Details:', logData);
      console.groupEnd();
    }
    
    // In production, you might want to send to analytics service
    if (this.isProduction) {
      this.sendToAnalytics('api_request', logData);
    }
    
    return requestId;
  }
  
  // API Response Logging
  static logApiResponse(
    service: string, 
    method: string, 
    endpoint: string, 
    response: any, 
    duration: number, 
    requestId?: string
  ) {
    const timestamp = new Date().toISOString();
    const userContext = this.getUserContext();
    
    const logData: ApiResponseLog = {
      timestamp,
      service,
      method: method.toUpperCase(),
      endpoint,
      requestId,
      ...userContext,
      status: response?.status || 200,
      duration: Math.round(duration),
      dataSize: this.getDataSize(response?.data),
      success: true
    };
    
    if (this.isDevelopment) {
      const emoji = duration > 2000 ? '🐌' : duration > 1000 ? '⚡' : '🚀';
      console.group(`✅ ${emoji} [API SUCCESS] ${method.toUpperCase()} ${endpoint} (${Math.round(duration)}ms)`);
      console.log('📈 Response Details:', logData);
      console.log('📦 Response Data:', response?.data);
      console.groupEnd();
    }
    
    if (this.isProduction) {
      this.sendToAnalytics('api_response', logData);
    }
  }
  
  // API Error Logging
  static logApiError(
    service: string, 
    method: string, 
    endpoint: string, 
    error: any, 
    duration: number, 
    requestId?: string
  ) {
    const timestamp = new Date().toISOString();
    const userContext = this.getUserContext();
    
    const logData: ApiErrorLog = {
      timestamp,
      service,
      method: method.toUpperCase(),
      endpoint,
      requestId,
      ...userContext,
      status: error?.response?.status,
      error: error?.message || 'Unknown error',
      duration: Math.round(duration),
      serverResponse: error?.response?.data,
      stack: error?.stack
    };
    
    // S1513 OBS-S1513-001 FIX: was console.error unconditional → leaked error details +
    // server response payload to browser console in production. Since sendToAnalytics is
    // currently a no-op placeholder (no Sentry/Bugsnag wired), the console output had no
    // monitoring benefit — it only exposed internal error details to anyone with browser
    // dev tools open. Gating to DEV-only mirrors the success-path pattern (line 126).
    if (this.isDevelopment) {
      console.group(`❌ [API ERROR] ${method.toUpperCase()} ${endpoint} (${Math.round(duration)}ms)`);
      console.error('💥 Error Details:', logData);
      if (error?.response?.data) {
        console.error('🔴 Server Response:', error.response.data);
      }
      console.groupEnd();
    }

    // Always send errors to analytics (when wired up — currently no-op placeholder).
    this.sendToAnalytics('api_error', logData);
  }
  
  // User Action Logging
  static logUserAction(
    action: string, 
    entityType: string, 
    entityId?: number, 
    details?: any, 
    result: 'success' | 'error' | 'pending' = 'success'
  ) {
    const timestamp = new Date().toISOString();
    const userContext = this.getUserContext();
    
    const logData: UserActionLog = {
      timestamp,
      ...userContext,
      action,
      entityType,
      entityId,
      details: this.sanitizeData(details),
      result
    };
    
    if (this.isDevelopment) {
      const emoji = result === 'success' ? '✅' : result === 'error' ? '❌' : '⏳';
      console.log(`${emoji} [USER ACTION] ${action} ${entityType}${entityId ? ` #${entityId}` : ''}`, logData);
    }
    
    this.sendToAnalytics('user_action', logData);
  }
  
  // Performance Monitoring
  static logPerformance(operation: string, duration: number, details?: any) {
    const timestamp = new Date().toISOString();
    const userContext = this.getUserContext();
    
    const logData = {
      timestamp,
      ...userContext,
      operation,
      duration: Math.round(duration),
      details
    };
    
    if (this.isDevelopment) {
      const emoji = duration > 5000 ? '🐌' : duration > 2000 ? '⚡' : '🚀';
      console.log(`${emoji} [PERFORMANCE] ${operation} (${Math.round(duration)}ms)`, logData);
    }
    
    if (duration > 3000) { // Log slow operations
      this.sendToAnalytics('performance_slow', logData);
    }
  }
  
  // General Debug Logging
  static debug(message: string, data?: any) {
    if (this.isDevelopment) {
      console.log(`🔍 [DEBUG] ${message}`, data);
    }
  }
  
  // Warning Logging
  static warn(message: string, data?: any) {
    const timestamp = new Date().toISOString();
    const userContext = this.getUserContext();
    
    console.warn(`⚠️ [WARNING] ${message}`, { timestamp, ...userContext, data });
    
    this.sendToAnalytics('warning', { timestamp, ...userContext, message, data });
  }
  
  // Critical Error Logging
  static critical(message: string, error?: any) {
    const timestamp = new Date().toISOString();
    const userContext = this.getUserContext();
    
    console.error(`🚨 [CRITICAL] ${message}`, { timestamp, ...userContext, error });
    
    // Always send critical errors
    this.sendToAnalytics('critical_error', { timestamp, ...userContext, message, error });
  }
  
  // Data Sanitization (remove sensitive info)
  private static sanitizeData(data: any): any {
    if (!data) return data;
    
    const sensitiveKeys = ['password', 'token', 'secret', 'key', 'authorization'];
    
    if (typeof data === 'object') {
      const sanitized = { ...data };
      
      for (const key in sanitized) {
        if (sensitiveKeys.some(sensitive => key.toLowerCase().includes(sensitive))) {
          sanitized[key] = '[REDACTED]';
        }
      }
      
      return sanitized;
    }
    
    return data;
  }
  
  // Sanitize Headers
  private static sanitizeHeaders(headers: any): any {
    if (!headers) return headers;
    
    const sanitized = { ...headers };
    const sensitiveHeaders = ['authorization', 'x-api-key', 'cookie'];
    
    for (const header in sanitized) {
      if (sensitiveHeaders.includes(header.toLowerCase())) {
        sanitized[header] = '[REDACTED]';
      }
    }
    
    return sanitized;
  }
  
  // Calculate data size
  private static getDataSize(data: any): number {
    try {
      return new Blob([JSON.stringify(data)]).size;
    } catch {
      return 0;
    }
  }
  
  // Send to Analytics Service (placeholder)
  private static sendToAnalytics(eventType: string, data: any) {
    // In production, this could send to:
    // - Google Analytics
    // - Application Insights
    // - Custom analytics endpoint
    // - Error tracking service (Sentry, Bugsnag)
    
    if (this.isProduction) {
      // Example: Send to your backend analytics endpoint
      // fetch('/api/analytics/log', {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify({ eventType, data })
      // }).catch(() => {}); // Silent fail for analytics
    }
  }
  
  // API Call Wrapper with automatic logging
  static async wrapApiCall<T>(
    service: string,
    method: string,
    endpoint: string,
    apiCall: () => Promise<T>,
    params?: any,
    data?: any
  ): Promise<T> {
    const requestId = this.logApiRequest(service, method, endpoint, params, data);
    const startTime = performance.now();
    
    try {
      const result = await apiCall();
      const duration = performance.now() - startTime;
      this.logApiResponse(service, method, endpoint, result, duration, requestId);
      return result;
    } catch (error) {
      const duration = performance.now() - startTime;
      this.logApiError(service, method, endpoint, error, duration, requestId);
      throw error;
    }
  }
}

export default Logger;